import {
    APP_BASE_HREF,
    CommonModule,
    Location,
    LocationStrategy,
    PathLocationStrategy,
    registerLocaleData
} from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import localeEnExtra from '@angular/common/locales/extra/en';
import {
    APP_INITIALIZER,
    ApplicationRef,
    Injectable,
    Injector,
    LOCALE_ID,
    NgModule,
    provideZoneChangeDetection,
} from '@angular/core';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbDatepickerI18n, NgbDatepickerI18nDefault } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { EntityDataModule } from '@ngrx/data';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule, TranslateParser, TranslateService } from '@ngx-translate/core';
import { AngularTranslateCompatParser } from './core/i18n/angular-translate-compat-parser';
import { InBundleTranslateLoader } from './core/i18n/in-bundle-translate-loader';
import { UIRouterModule } from '@uirouter/angular';
import { StateRegistry, trace } from '@uirouter/core';
import { ScrollRestorationService } from 'account-hybrid/common/services/scroll-restoration.service';
import { CacheInterceptor } from 'account-hybrid/core/interceptors/cache.interceptor';
import { ContextSelectorModule } from 'account-hybrid/features/context-selector/context-selector.module';
import { rootEffects } from 'account-hybrid/store/effects';
import { RootStoresModule } from 'account-hybrid/store/root-stores.module';
import {
    AccountSharedModule,
    AreasRegistry,
    MultipleOptionsRegistryItem,
    StateRegistryItem
} from 'apps/account/src/account-shared';
// §4 esbuild: default import (not `* as`) — browser-update is CommonJS (module.exports = fn);
// esbuild's `import * as` yields a non-callable namespace object → "will crash at run-time".
import browserUpdate from 'browser-update';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AppRootComponent } from './app-root.component';
import { AccountCommonModule } from './common/common.module';
import { AccountManagementModule } from './common/modules/account-management/account-management.module';
import { getSpaceAttributesFromSpace, SpacesService, SpaceType } from './common/components/spaces';
import { PasswordStrengthModule } from './common/modules/password-strength/password-strength.module';
import { IconsService } from './common/services/icons.service';
import {
    APP_MENU_AREAS_REGISTRY,
    AREAS_REGISTRY
} from './config/bootstrap-angularjs.config';
import { configureRouterHooks, routerOtherwise } from './config/router-hooks.config';
import {
    ADMIN_STATES,
    COLLABORATIVE_COMPANY_SPACE_STATES,
    COMPANY_SPACE_STATES,
    GLOBAL_STATES,
    MIXED_SPACE_STATES,
    PROPERTY_SPACE_STATES
} from './config/states.config';
import { CompanyType, IdentityService, Permissions, SessionService } from './core/authentication';
import { Environment, ENVIRONMENT_TOKEN } from './core/environment.service';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { ParamInterceptor } from './core/interceptors/param.interceptor';
import { HttpXsrfInterceptor } from './core/interceptors/xsrf.interceptor';
import { LicenseService } from './core/license.service';
import { PlatformStateService } from './core/platform-state.service';
import { ShortUrlsModule } from './downgraded/short-urls/short-urls.module';
import { AuthenticationModule } from './features/authentication/authentication.module';
import { PeopleModule } from './features/people/people.module';
import { AdvocacyGroupModule } from './features/advocacy-group/advocacy-group.module';
import { COOPERATIVE_STATE } from './features/cooperative/cooperative.states';
import { COOPERATIVE_LEGACY_FEATURE, cooperativeLegacyReducers } from './features/cooperative/store/cooperative-legacy.reducer';
import { metaReducers, reducers } from './store/reducers';

trace.enable(1, 5);
registerLocaleData(localeEn, 'en', localeEnExtra);

declare let ENV, LOGIN_URL, API_BASE_URL, API_V1_BASE_URL, V1_BASE_URL, V1_HOME_PAGE_URL,
    GOOGLE_PLACES_API_KEY, ACCOUNT_VERSION;
declare let FliptoHubDomain, FliptoBaseHelpCenterUrl, FliptoPlatformDomain, AzureCdnBaseUrl, CurationShareContentSize;
declare const environmentSettings;

declare const angular, ace;

/**
 * ng-bootstrap >=17 datepicker weekday labels use Intl widths (narrow 'S' by default);
 * the pre-upgrade look was Angular's 2-letter locale short names ('Su','Mo',…).
 * Intl 'short' gives 'Sun'/'Mon' — slice to the first two characters to restore it.
 */
@Injectable()
export class FtNgbDatepickerI18n extends NgbDatepickerI18nDefault {
    override getWeekdayLabel(weekday: number): string {
        return super.getWeekdayLabel(weekday, 'short').slice(0, 2);
    }
}

@NgModule({
    declarations: [
        AppComponent,
        AppRootComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        CommonModule,
        HttpClientModule,
        HttpClientXsrfModule.withOptions({
            cookieName: 'XSRF-TOKEN',
            headerName: 'X-XSRF-TOKEN',
        }),
        FormsModule,
        // WS2: native ngx-translate runtime serving the SAME `en` dictionary as the
        // AngularJS angular-translate, with an angular-translate-compatible parser, so
        // native components resolve translations through the native ngx runtime.
        // Additive — consumers are repointed off the bridge incrementally (gated).
        TranslateModule.forRoot({
            defaultLanguage: 'en',
            loader: { provide: TranslateLoader, useClass: InBundleTranslateLoader },
            parser: { provide: TranslateParser, useClass: AngularTranslateCompatParser },
        }),
        UIRouterModule.forRoot({
            states: GLOBAL_STATES,
            deferIntercept: true,
            otherwise: routerOtherwise,
            config: configureRouterHooks
        }),
        StoreModule.forRoot(reducers, {
            metaReducers,
            runtimeChecks: {
                strictStateImmutability: false,
                strictActionImmutability: false
            }
        }),
        // WS2 strangle: the cooperative section's state (was ng-redux
        // views.cooperative + apiData.propertiesStatistics) now lives here. Registered
        // eagerly: the cooperative ROUTE itself is registered conditionally in
        // ngDoBootstrap (cooperative-type company spaces only), so it can't host this.
        StoreModule.forFeature(COOPERATIVE_LEGACY_FEATURE, cooperativeLegacyReducers),
        StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production }),
        EffectsModule.forRoot(rootEffects),
        EntityDataModule.forRoot({}),
        RootStoresModule,
        ShortUrlsModule,
        NgSelectModule,
        MatIconModule,
        AccountSharedModule,
        AccountCommonModule,
        // WS5 Phase 2: AppRootComponent's chrome renders <ft-create-property>/<ft-create-account>
        // natively (previously downgrade bridges inside app.html). Already eager via Spaces/
        // Navigation modules → Angular module dedup means no duplicate forFeature/effects.
        AccountManagementModule,
        ContextSelectorModule,
        AuthenticationModule,
        PeopleModule,
        AdvocacyGroupModule,
        PasswordStrengthModule.forRoot({
            minLength: 6,
            minNumbersLength: 1,
            minSpecialsLength: 1,
            minUppercaseLength: 1,
            minLowercaseLength: 1,
            numberOrSpecials: true,
            noSpaces: true
        }),
        MatSnackBarModule
    ],
    providers: [
        // Angular 21 flipped the change-detection default to ZONELESS (NoopNgZone). This hybrid
        // app is zone-based (NgRx/RxJS + AngularJS interop), so opt back into zone-based CD to
        // restore the pre-21 behavior — without it, async store/HTTP emissions never trigger a CD
        // tick and views stay stuck on their initial state. (Other apps use provideZonelessChangeDetection.)
        provideZoneChangeDetection(),
        // ng-bootstrap >=17 switched datepicker weekday labels from Angular locale short names
        // ('Su','Mo',…) to Intl narrow ('S','M',…). Every ft datepicker was designed around the
        // 2-letter look — restore it app-wide (Intl 'short' = 'Sun' → slice to 'Su').
        { provide: NgbDatepickerI18n, useClass: FtNgbDatepickerI18n },
        // CDK 21 defaults overlays to the Popover API, which moves the backdrop INSIDE the
        // .cdk-global-overlay-wrapper. All the app's overlay theming (e.g. shared-assets
        // _mat-dialog.scss `.cdk-overlay-backdrop.-dark + .cdk-global-overlay-wrapper ...`)
        // keys on the pre-21 sibling DOM, so dialogs rendered as unthemed white surfaces.
        // Opt back into the classic overlay DOM app-wide.
        { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
        {
            provide: APP_INITIALIZER,
            useFactory: function (httpClient: HttpClient, identityService: IdentityService) {
                return () => {
                    return new Promise<void>((resolve) => {
                        if (!identityService.isAnonymous()) {
                            httpClient.get('/api/auth/ping')
                                .subscribe(() => resolve);
                        }
                        resolve(null);
                    });
                };
            },
            deps: [HttpClient, IdentityService],
            multi: true
        },
        {
            // Deliberately fire-and-forget: this returns void rather than a Promise, so Angular does
            // not await it and platform-state never sits in front of ngDoBootstrap (which is what
            // registers the lazy feature states — awaiting here would put this round trip ahead of
            // every property-space page, spacetime included).
            //
            // It reads as blocking but never was: the previous version resolved its Promise
            // unconditionally and synchronously, which made the trailing `.finally(resolve)` dead
            // code. The stories, discovery and people tabs read platformStateService.state, all
            // through template bindings, so a late arrival re-renders rather than breaking. (The
            // service is also downgraded for AngularJS at the bottom of this file, so treat those as
            // the known consumers rather than a set closeable by inspection.) Keep it non-blocking;
            // if a consumer ever needs to await the value, give it an observable instead of making
            // bootstrap wait.
            provide: APP_INITIALIZER,
            useFactory: function (platformStateService: PlatformStateService, identityService: IdentityService, spacesService: SpacesService) {
                return () => {
                    if (identityService.isAnonymous() || !spacesService.hasPropertyContext()) {
                        return;
                    }
                    const propertyUuid = spacesService.getPropertyUuid();
                    platformStateService.load(propertyUuid)
                        .then((data) => {
                            platformStateService.state = data;
                        })
                        // Nothing awaits this, so a rejection would otherwise surface only as an
                        // unhandled rejection. The tabs that read `state` treat absent as hidden, so
                        // failing is survivable — but log it, or the tabs silently stay hidden with
                        // no trace of why.
                        .catch(err => console.error('platform-state load failed', err));
                };
            },
            deps: [PlatformStateService, IdentityService, SpacesService],
            multi: true
        },
        { provide: APP_BASE_HREF, useValue: '/' },
        {
            provide: ENVIRONMENT_TOKEN, useFactory: () => {
                return {
                    name: environmentSettings.ENV || ENV,
                    loginUrl: environmentSettings.LOGIN_URL || LOGIN_URL,
                    apiBaseUrl: environmentSettings.API_BASE_URL || API_BASE_URL,
                    apiV1BaseUrl: environmentSettings.API_V1_BASE_URL || API_V1_BASE_URL,
                    v1BaseUrl: environmentSettings.V1_BASE_URL || V1_BASE_URL,
                    v1HomePageUrl: environmentSettings.V1_HOME_PAGE_URL || V1_HOME_PAGE_URL,
                    FliptoHubDomain: environmentSettings.FliptoHubDomain || FliptoHubDomain,
                    FliptoBaseHelpCenterUrl: environmentSettings.FliptoBaseHelpCenterUrl || FliptoBaseHelpCenterUrl,
                    FliptoPlatformDomain: environmentSettings.FliptoPlatformDomain || FliptoPlatformDomain,
                    AzureCdnBaseUrl: environmentSettings.AzureCdnBaseUrl || AzureCdnBaseUrl,
                    CurationShareContentSize: environmentSettings.CurationShareContentSize || CurationShareContentSize,
                    systemCompanyUuid: '761365AA-A432-4BF1-A29C-3787BA94A4A9', // TODO Update this to correct one!
                    version: environmentSettings.ACCOUNT_VERSION || ACCOUNT_VERSION,
                    googlePlacesApiKey: environmentSettings.GOOGLE_PLACES_API_KEY || GOOGLE_PLACES_API_KEY,
                    cloudbedsBaseUrl: environmentSettings.CloudbedsBaseUrl,
                    cloudbedsClientId: environmentSettings.CloudbedsClientId,
                } as Environment;
            }
        },
        { provide: AREAS_REGISTRY, useClass: AreasRegistry, deps: [] },
        { provide: 'AreasRegistry', useExisting: AREAS_REGISTRY },
        { provide: APP_MENU_AREAS_REGISTRY, useClass: AreasRegistry, deps: [] },
        { provide: 'AppMenuAreasRegistry', useExisting: APP_MENU_AREAS_REGISTRY },
        // { provide: MOBILE_MENU_AREAS_REGISTRY, useClass: AreasRegistry, deps: [] },
        // { provide: 'MobileMenuAreasRegistry', useExisting: MOBILE_MENU_AREAS_REGISTRY },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ParamInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpXsrfInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CacheInterceptor,
            multi: true
        },
        { provide: LOCALE_ID, useValue: 'en' },
        Location, { provide: LocationStrategy, useClass: PathLocationStrategy },
        // FTK-7242 Phase 5b: PrimeNG is gone entirely — no `<p-table>`, no MessageService/`<p-toast>`
        // (FtToastService/MatSnackBar replaced it), so `providePrimeNG` and its theme are gone too.
        // NOTE: stats-table still uses the primeng CLASS NAMES (p-datatable*, p-paginator*) on purpose,
        // but supplies those rules itself as unlayered CSS in its own component SCSS — it does not
        // depend on PrimeNG's styled mode.
    ]
})
export class AppModule {
    constructor(
        private injector: Injector,
        private icons: IconsService,
        private translate: TranslateService
    ) {
        // Activate the single account UI locale (parity with the former AngularJS
        // `$translateProvider.preferredLanguage('en')`). The loader is synchronous, so
        // `instant()`/the pipe resolve immediately.
        this.translate.setDefaultLang('en');
        this.translate.use('en');
    }

    async ngDoBootstrap() {

        this.icons.register();

        const spacesService = this.injector.get(SpacesService);
        const identity = this.injector.get(IdentityService);
        const stateRegistry = this.injector.get(StateRegistry);
        const areasRegistry = this.injector.get(AREAS_REGISTRY) as AreasRegistry,
            appMenuAreasRegistry = this.injector.get(APP_MENU_AREAS_REGISTRY) as AreasRegistry;
        const scrollRestorationService = this.injector.get(ScrollRestorationService);

        scrollRestorationService.init();

        const addStateToAreasRegistry = (state: StateRegistryItem, activeWhen?: string) => {
            if (activeWhen) {
                state.activeWhen = activeWhen;
            }
            areasRegistry.add(state);
            appMenuAreasRegistry.add(state);
        };

        if (!identity.isAnonymous()) {
            if (identity.isFliptoStaff()) {
                ADMIN_STATES.forEach(state => {
                    stateRegistry.register(state);
                });
            }

            if (spacesService.current) {
                appMenuAreasRegistry.add(new StateRegistryItem('Overview', 'ft-navitem', 'hub', getSpaceAttributesFromSpace(spacesService.current), 0));
            }

            switch (spacesService.current?.type) {
                case SpaceType.Company: {
                    COMPANY_SPACE_STATES.forEach(state => {
                        stateRegistry.register(state);
                    });

                    // v1 gate (account-v2 features/index.ts → coop.features.ts): the cooperative
                    // route only ever existed on a Company space whose company is Cooperative-type.
                    // Mixed spaces never registered it, so /m/{slug}/cooperative falls through to
                    // the `otherwise` rule (space overview) exactly as on master.
                    const sessionService = this.injector.get(SessionService);
                    const company = sessionService.getCompany(spacesService.getCompanyUuid());
                    if (company?.Type === CompanyType.Cooperative) {
                        stateRegistry.register(COOPERATIVE_STATE);
                    }

                    addStateToAreasRegistry(
                        new StateRegistryItem('Account settings', 'ft-navitem', 'account-settings', getSpaceAttributesFromSpace(spacesService.current), 10),
                        'account-settings.**'
                    );
                    addStateToAreasRegistry(
                        new StateRegistryItem('Discovery', 'ft-navitem', 'discovery.big-picture', getSpaceAttributesFromSpace(spacesService.current), 2, true),
                        'discovery.**'
                    );
                    addStateToAreasRegistry(
                        new StateRegistryItem('Team', 'ft-navitem', 'team', getSpaceAttributesFromSpace(spacesService.current), 6),
                        'team.**'
                    );
                    break;
                }
                case SpaceType.Property: {
                    PROPERTY_SPACE_STATES.forEach(state => {
                        stateRegistry.register(state);
                    });

                    addStateToAreasRegistry(
                        new StateRegistryItem('Discovery', 'ft-navitem', 'discovery.big-picture', getSpaceAttributesFromSpace(spacesService.current), 2),
                        'discovery.**'
                    );
                    addStateToAreasRegistry(
                        new StateRegistryItem('Incentives', 'ft-navitem', 'incentives', getSpaceAttributesFromSpace(spacesService.current), 6),
                        'incentives.**'
                    );
                    break;
                }
                case SpaceType.Mixed: {
                    MIXED_SPACE_STATES.forEach(state => {
                        stateRegistry.register(state);
                    });

                    addStateToAreasRegistry(
                        new StateRegistryItem('Discovery', 'ft-navitem', 'discovery.big-picture', getSpaceAttributesFromSpace(spacesService.current), 2),
                        'discovery.**'
                    );

                    addStateToAreasRegistry(
                        new StateRegistryItem('Account settings', 'ft-navitem', 'account-settings', getSpaceAttributesFromSpace(spacesService.current), 10),
                        'account-settings.**'
                    );
                    addStateToAreasRegistry(
                        new StateRegistryItem('Incentives', 'ft-navitem', 'incentives', getSpaceAttributesFromSpace(spacesService.current), 6),
                        'incentives.**'
                    );
                    addStateToAreasRegistry(
                        new StateRegistryItem('Team', 'ft-navitem', 'team', getSpaceAttributesFromSpace(spacesService.current), 7),
                        'team.**'
                    );

                    break;
                }
                case SpaceType.Collaborative: {
                    COLLABORATIVE_COMPANY_SPACE_STATES.forEach(state => {
                        stateRegistry.register(state);
                    });

                    const moreSubStates = [];

                    if (identity.isFliptoStaff()) {
                        const state = new StateRegistryItem('Collaborative settings', 'ft-navitem', 'account-settings', getSpaceAttributesFromSpace(spacesService.current), 6);
                        appMenuAreasRegistry.add(state);
                        moreSubStates.push(state);
                    }

                    if (identity.isFliptoStaff()) {
                        const membersState = new StateRegistryItem('Members', 'ft-navitem', 'members', getSpaceAttributesFromSpace(spacesService.current), 5);
                        appMenuAreasRegistry.add(membersState);
                        moreSubStates.push(membersState);
                    }

                    if (identity.isFliptoStaff() && identity.hasPermission(Permissions.UserManagementRead)) {
                        const state = new StateRegistryItem('Team', 'ft-navitem', 'team', getSpaceAttributesFromSpace(spacesService.current), 4);
                        appMenuAreasRegistry.add(state);
                        moreSubStates.push(state);
                    }

                    if (moreSubStates.length > 0) {
                        areasRegistry.add(new MultipleOptionsRegistryItem('Membership', 'ft-navitem', moreSubStates, 4));
                    }


                    const states = [
                        new StateRegistryItem(
                            'Insights',
                            'ft-navitem',
                            'collaborative.insights',
                            getSpaceAttributesFromSpace(spacesService.current),
                            2),
                        new StateRegistryItem(
                            'Resources',
                            'ft-navitem',
                            'collaborative.resources',
                            getSpaceAttributesFromSpace(spacesService.current),
                            3,
                            true)
                    ];
                    states.forEach(state => {
                        areasRegistry.add(state);
                        appMenuAreasRegistry.add(state);
                    });
                    break;
                }
            }
        }

        // WS5 Phase 2: bootstrap the native Angular root LAST — after every space-state is
        // registered — so its <ui-view> can immediately resolve the active state once
        // main.ts re-enables URL interception (urlService.listen/sync).
        this.injector.get(ApplicationRef).bootstrap(AppRootComponent, document.querySelector('ft-account-app'));

        browserUpdate({
            required: { e: -5, f: -5, o: -5, s: -5, c: -5 },
            insecure: true,
            unsupported: true,
            api: 2020.09,
            l: "en",
            shift_page_down: false,
            nostatistics: true,
            text: { 'msgmore': 'You may experience issues using your Flip.to account on older browsers.' }
        })
    }
}
